/**
 * Composant d'affichage du statut de paiement Mobile Money
 * Affiche une UI pendant que l'utilisateur confirme sur son téléphone
 */

import React from 'react';
import { MobileMoneyState, MobileMoneyStatus } from '../../hooks/useMobileMoneyPayment';

interface MobileMoneyStatusDisplayProps {
  state: MobileMoneyState;
  onCancel?: () => void;
  onRetry?: () => void;
  onClose?: () => void;
}

// Icônes par statut
const StatusIcon: React.FC<{ status: MobileMoneyStatus }> = ({ status }) => {
  switch (status) {
    case 'pending':
    case 'processing':
      return (
        <div className="relative">
          <div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl">📱</span>
          </div>
        </div>
      );
    case 'completed':
      return (
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    case 'failed':
    case 'expired':
    case 'cancelled':
      return (
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    default:
      return null;
  }
};

// Barre de progression du temps
const TimeProgressBar: React.FC<{ elapsed: number; remaining: number }> = ({ elapsed, remaining }) => {
  const total = elapsed + remaining;
  const progress = total > 0 ? (elapsed / total) * 100 : 0;
  
  // Couleur selon le temps restant
  let barColor = 'bg-primary';
  if (remaining < 60) barColor = 'bg-orange-500';
  if (remaining < 30) barColor = 'bg-red-500';

  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>Temps écoulé: {formatTime(elapsed)}</span>
        <span>Restant: {formatTime(remaining)}</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${barColor} transition-all duration-1000 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

// Formater le temps en MM:SS
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Mappage des providers vers des noms affichables et codes USSD
// Conforme à la documentation backend
const PROVIDER_INFO: Record<string, { name: string; ussd: string }> = {
  // Codes télécom backend (AM, OM, MP, AF)
  'AM': { name: 'Airtel Money', ussd: '*150#' },
  'OM': { name: 'Orange Money', ussd: '#150#' },
  'MP': { name: 'M-Pesa', ussd: '*100#' },
  'AF': { name: 'Africell', ussd: '*144#' },
  // Noms alternatifs
  'mpesa': { name: 'M-Pesa', ussd: '*100#' },
  'orange': { name: 'Orange Money', ussd: '#150#' },
  'airtel': { name: 'Airtel Money', ussd: '*150#' },
  'vodacom': { name: 'Vodacom M-Pesa', ussd: '*100#' },
  'africell': { name: 'Africell Money', ussd: '*144#' },
  'airtel money': { name: 'Airtel Money', ussd: '*150#' },
  'orange money': { name: 'Orange Money', ussd: '#150#' },
  'm-pesa': { name: 'M-Pesa', ussd: '*100#' }
};

// Fonction pour obtenir les infos du provider
function getProviderInfo(provider: string | null): { name: string; ussd: string } {
  if (!provider) return { name: 'Mobile Money', ussd: '*150#' };
  const normalized = provider.toLowerCase();
  return PROVIDER_INFO[provider] || PROVIDER_INFO[normalized] || { name: provider, ussd: '*150#' };
}

const MobileMoneyStatusDisplay: React.FC<MobileMoneyStatusDisplayProps> = ({
  state,
  onCancel,
  onRetry,
  onClose
}) => {
  const { status, message, provider, phoneNumber, elapsedTime, remainingTime } = state;

  // Ne rien afficher si idle
  if (status === 'idle') return null;

  const providerInfo = getProviderInfo(provider);
  const maskedPhone = phoneNumber ? maskPhoneNumber(phoneNumber) : '';

  const isPending = status === 'pending' || status === 'processing';
  const isError = status === 'failed' || status === 'expired' || status === 'cancelled';
  const isSuccess = status === 'completed';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 animate-fade-in">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <StatusIcon status={status} />
          </div>
          
          <h3 className={`text-xl font-bold mb-2 ${
            isSuccess ? 'text-green-600' : 
            isError ? 'text-red-600' : 
            'text-gray-900'
          }`}>
            {isSuccess && 'Paiement réussi !'}
            {isError && (status === 'expired' ? 'Délai expiré' : 'Paiement échoué')}
            {isPending && 'Confirmation en attente'}
          </h3>
          
          <p className="text-gray-600">
            {message}
          </p>
        </div>

        {/* Informations du paiement */}
        {(provider || phoneNumber) && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Opérateur</span>
              <span className="font-medium text-gray-900">{providerInfo.name}</span>
            </div>
            {maskedPhone && (
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-gray-500">Numéro</span>
                <span className="font-medium text-gray-900">{maskedPhone}</span>
              </div>
            )}
            {isPending && (
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-gray-500">Code USSD</span>
                <span className="font-mono font-bold text-primary">{providerInfo.ussd}</span>
              </div>
            )}
          </div>
        )}

        {/* Barre de progression (uniquement si en attente) */}
        {isPending && (
          <div className="mb-6">
            <TimeProgressBar elapsed={elapsedTime} remaining={remainingTime} />
          </div>
        )}

        {/* Instructions (si en attente) */}
        {isPending && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
              <span>📲</span> Instructions
            </h4>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Vérifiez votre téléphone pour la notification</li>
              <li>Entrez votre code PIN {providerInfo.name}</li>
              <li>Confirmez le paiement</li>
              <li className="text-blue-500">
                Ou composez <span className="font-mono font-bold">{providerInfo.ussd}</span> pour vérifier
              </li>
            </ol>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          {isPending && onCancel && (
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
          )}
          
          {isError && onRetry && (
            <button
              onClick={onRetry}
              className="flex-1 px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Réessayer
            </button>
          )}
          
          {(isSuccess || isError) && onClose && (
            <button
              onClick={onClose}
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                isSuccess 
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {isSuccess ? 'Continuer' : 'Fermer'}
            </button>
          )}
        </div>

        {/* Message d'aide (si erreur) */}
        {isError && (
          <p className="text-center text-xs text-gray-500 mt-4">
            Besoin d'aide ? Contactez notre support via WhatsApp
          </p>
        )}
      </div>
    </div>
  );
};

// Masquer partiellement le numéro de téléphone
function maskPhoneNumber(phone: string): string {
  if (phone.length < 6) return phone;
  const start = phone.slice(0, 4);
  const end = phone.slice(-3);
  return `${start}***${end}`;
}

export default MobileMoneyStatusDisplay;
